/* eslint-disable no-console */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import { useMatchesData } from '~/hooks/data/use-matches-data'
import { useShop } from '~/hooks/data/use-shop'
import type { ShopShippingOptions } from '~/schemas/shop'

interface OnboardingStep {
  id: string
  title: string
  checked: boolean
  link: string
}

interface OnboardingChecklist {
  steps: OnboardingStep[]
  allCompleted: boolean
}

export function useOnboardingChecklist(): OnboardingChecklist {
  const { t } = useTranslation()
  const shop = useShop()
  const data = useMatchesData('routes/__shop') as
    | {
        metadata?: { productsCount?: { published?: number } }
      }
    | undefined
  const published = data?.metadata?.productsCount?.published ?? 0

  const steps = useMemo(() => {
    const shippingOptions = shop.shippingOptions as ShopShippingOptions

    return [
      {
        id: 'logo_name',
        title: t('page.settings.visibility.steps.logo_name'),
        checked: Boolean(shop.avatarSrc),
        link: makeShopOwnPath(shop, '/?onboarding=avatar'),
      },
      {
        id: 'support_email',
        title: t('page.settings.visibility.steps.support_email'),
        checked: Boolean(shop.supportEmail),
        link: makeShopOwnPath(shop, '/?onboarding=support_email'),
      },
      {
        id: 'first_product',
        title: t('page.settings.visibility.steps.first_product'),
        checked: published > 0,
        link: makeShopOwnPath(shop, '/products/create'),
      },
      {
        id: 'provider',
        title: t('page.settings.visibility.steps.provider'),
        checked: shop.stripeAccountStatus === 'ready',
        link: makeShopOwnPath(
          shop,
          '/settings/general?onboarding=payment-provider'
        ),
      },
      {
        id: 'shipping_options',
        title: t('page.settings.visibility.steps.shipping_options'),
        checked: Boolean(shippingOptions && shippingOptions.length > 0),
        link: makeShopOwnPath(shop, '/settings/shipping'),
      },
      {
        id: 'legal',
        title: t('page.settings.visibility.steps.legal'),
        checked:
          !!shop.termsAndConditions &&
          !!shop.privacyPolicy &&
          !!shop.returnPolicy,
        link: makeShopOwnPath(shop, '/legal'),
      },
    ]
  }, [t, shop, published])

  const allCompleted = useMemo(() => {
    return steps.every((step) => step.checked)
  }, [steps])

  return {
    steps,
    allCompleted,
  }
}
